import { computed, reactive, ref } from 'vue'
import cloneDeep from 'lodash.clonedeep'

import {
  getOperationForType,
  getOperationRecordTemplate,
  getOperationValidatorForType,
} from './constants/operationTypes'

/**
 * A service for the management of operation data
 * @param {Object} service SFTP settings service for persistence operations
 * @returns {*}
 */
export default (service) => {
  const operation = ref(null)
  const mode = ref(null)
  const serviceData = new Map() // service data initialised by a single operation type that can be shared amongst other dependents
  const working = ref(false)
  const apiErrors = ref([]) // backend validation report

  return {
    /**
     * Return the current drilled down account
     * @returns {*}
     */
    getAccount() {
      return service.account
    },

    /**
     * Return the current drilled down agency
     * @returns {*}
     */
    getAgency() {
      return service.agency
    },

    /**
     * Return a list of preset lead fields
     * @returns {*}
     */
    getPresetLeadFields() {
      return service.getPresetLeadFields()
    },

    /**
     * Get working state
     * @returns {ComputedRef<*>}
     */
    get working() {
      return computed(() => working.value)
    },

    /**
     * Determine if an operation is under management
     * @returns {ComputedRef<*>}
     */
    get active() {
      return computed(
        () =>
          operation.value !== null && ['create', 'update'].includes(mode.value),
      )
    },

    /**
     * Return the value of the operation
     * @returns {ComputedRef<*>}
     */
    getOperation() {
      return operation.value
    },

    /**
     * Return the current mode of operation
     * @returns {ComputedRef<*>}
     */
    get mode() {
      return computed(() => mode.value)
    },

    /**
     * Return a reference to the interface to configure the assigned operation
     * @returns {ComputedRef<*>}
     */
    get configurator() {
      return computed(
        () =>
          this.active.value &&
          getOperationForType(operation.value.type).configurator,
      )
    },

    /**
     * Return an operation by the given uuid
     * @param {String} uuid
     * @returns {*}
     */
    entryByUUID(uuid) {
      return service.inboundOperations.value.find(
        (operation) => operation.uuid === uuid,
      )
    },

    /**
     * Initialise the service to handle the creation of a new operation
     * @param {String} type
     */
    initForNew(type) {
      operation.value = reactive(getOperationRecordTemplate(type))
      mode.value = 'create'
    },

    /**
     * Initialise the service to handle updates to an existing operation
     * @param {String} uuid
     */
    initForUUID(uuid) {
      const entry = this.entryByUUID(uuid)
      operation.value = reactive(cloneDeep(entry))
      mode.value = 'update'
    },

    /**
     * Initialise the service to perform a deletion of an existing operation
     * @param {String} uuid
     */
    deleteByUUID(uuid) {
      const entry = this.entryByUUID(uuid)
      operation.value = reactive(cloneDeep(entry))
      mode.value = 'delete'
      this.process()
    },

    /**
     * Reset the service to the initial state
     */
    reset() {
      operation.value = null
      mode.value = null
    },

    /**
     * Patch operation data
     * @param {Object} patch
     */
    patchOperation(patch = {}) {
      Object.keys(patch).forEach((k) => (operation.value[k] = patch[k]))
    },

    /**
     * Return service data for the given key
     * @param {String} key
     * @param {*=} fallback
     * @param {function=} init
     * @returns {ComputedRef<*>}
     */
    getServiceData(key, fallback = null, init) {
      if (!serviceData.has(key)) {
        serviceData.set(key, ref(fallback))
        if (init) init().then((value) => (serviceData.get(key).value = value))
      }
      return computed(() => serviceData.get(key).value)
    },

    /**
     * Set service data for the given key
     * @param {String} key
     * @param {*} value
     */
    setServiceData(key, value) {
      serviceData.set(key, value)
    },

    /**
     * Return the validation status of the referenced operation *LOCAL SHAPE VALIDATION*
     * @returns {ComputedRef<*>}
     */
    get validationStatus() {
      return computed(
        () =>
          (!!operation.value &&
            Object.getPrototypeOf(operation.value) === Object.prototype &&
            getOperationValidatorForType(operation.value.type)(
              operation.value,
            )) || { valid: false },
      )
    },

    /**
     * Determine the overall validity of the referenced operation *LOCAL SHAPE VALIDATION*
     * @returns {ComputedRef<*>}
     */
    get validationOutcome() {
      return computed(() => this.validationStatus.value.valid)
    },

    /**
     * Persist the referenced inbound operation
     * @param {boolean=} withReset
     */
    process(withReset = true) {
      working.value = true
      apiErrors.value = []
      service
        .processInboundOperation(mode.value, operation.value)
        .then((result = {}) => {
          if (result.success) {
            withReset ? this.reset() : null
          } else if (result.errors instanceof Array) {
            apiErrors.value = result.errors.slice(0) // backend validation report
          }
        })
        .catch(() => {})
        .finally(() => (working.value = false))
    },
  }
}
